import { DateTime } from '@ksyos/date-time';
import type { IEpisodeCategoryBasic } from '@common/interfaces/episodeCategory';
import type { IBreadcrumbItem } from '@frontend/components/elements/common';
import type { IWithRouterProps } from '@frontend/components/elements/HoC/withRouter';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { serviceReaderAddEditSchema } from '@common/validation/serviceReader';
import {
    CancelButton,
    DateInput,
    Dropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputGroup,
    OverlapMessage,
    SaveSubmit,
} from '@frontend/components/elements/common';
import { Component } from '@frontend/components/elements/Component';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { withRouter } from '@frontend/components/elements/HoC/withRouter';
import { FixedWidthTemplate } from '@frontend/components/templates/FixedWidthTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { OverlapChecker } from '@frontend/utils/overlapChecker';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';

type ServiceReaderAddEditProps = IWithRouterProps;

interface IPractitioner {
    id?: string;
    nameFamily: string;
    nameGiven: string;
    nameInfix: string;
}

interface IPractitionerEmployment {
    id: string;
    organization: {
        id: string;
        name: string;
    };
}

interface IServiceReader {
    employmentId: string | null;
    endDate: string | null;
    episodeCategoryId: string | null;
    startDate: string | null;
}

interface IServiceReaderAddEditState {
    episodeCategories: IEpisodeCategoryBasic[];
    episodeCategoryName?: string;
    errors: ValidationErrors;
    isSaving: boolean;
    overlap?: {
        endDate: string | null;
        id: string;
        startDate: string;
    };
    practitioner?: IPractitioner;
    practitionerEmployments: IPractitionerEmployment[];
    serviceReader?: IServiceReader;
}

class InnerServiceReaderAddEdit extends Component<ServiceReaderAddEditProps, IServiceReaderAddEditState> {
    private readonly overlapChecker = new OverlapChecker('/api/serviceReaders');

    constructor(props: ServiceReaderAddEditProps, context?: {}) {
        super(props, context);

        assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);

        if (!this.props.router.searchParams.get('practitionerId')) {
            throw new Error('No practitionerId');
        }

        this.state = {
            episodeCategories: [],
            practitionerEmployments: [],
            isSaving: false,
            errors: emptyValidationErrors,
        };
    }

    public async componentDidMount() {
        await this.setupComponent(this.props);
    }

    public render() {
        const practitionerId = this.props.router.searchParams.get('practitionerId');
        const breadcrumbs: IBreadcrumbItem[] = [{ text: translateKey('readers.readingService') }];
        if (this.getAddOrEdit() === 'edit') {
            breadcrumbs.push({ text: translateKey('common.edit') });
        } else {
            breadcrumbs.push({ text: translateKey('common.new') });
        }
        if (this.state.practitioner) {
            breadcrumbs.push({
                text: getDisplayName(this.state.practitioner),
                url: `/practitioners/${this.state.practitioner.id}`,
            });
        }
        const header =
            (this.getAddOrEdit() === 'edit' ? translateKey('common.edit') : translateKey('common.add')) +
            ' ' +
            translateKey('readers.readingService') +
            ' ' +
            (this.state.serviceReader && this.state.episodeCategoryName
                ? translateKey(`episodeCategoryEnum.${this.state.episodeCategoryName}`)
                : '');

        if (!this.state.serviceReader) {
            return null;
        }

        return (
            <FixedWidthTemplate breadcrumb={breadcrumbs}>
                <Header content={header} />
                <FormContextProvider
                    value={{
                        placeholderGroupName: 'common',
                        item: this.state.serviceReader,
                        errors: this.state.errors,
                        onChange: async (key, value) => await this.handleChange(key, value),
                        onDoValidate: async (key) => await this.validateOne(key),
                    }}
                >
                    <FormContainer onSubmit={async () => await this.save()}>
                        {this.state.overlap && (
                            <OverlapMessage
                                endDate={this.state.overlap.endDate}
                                link={`/serviceReaders/${this.state.overlap.id}?practitionerId=${practitionerId}`}
                                message={translateKey('supervisors.overlapMessage')}
                                startDate={this.state.overlap.startDate}
                            />
                        )}
                        <FormGroup hasErrors={this.state.errors.has('startDate')}>
                            <FormLabel isRequired>{translateKey('common.startDate')}</FormLabel>
                            <InputGroup>
                                <DateInput fieldKey="startDate" />
                            </InputGroup>
                        </FormGroup>
                        <FormGroup hasErrors={this.state.errors.has('endDate')}>
                            <FormLabel>{translateKey('common.endDate')}</FormLabel>
                            <InputGroup>
                                <DateInput fieldKey="endDate" />
                            </InputGroup>
                        </FormGroup>
                        {this.getAddOrEdit() === 'add' && (
                            <>
                                <FormHeader>
                                    {translateKey('employment.employment')} & {translateKey('service.service')}
                                </FormHeader>
                                <FormGroup hasErrors={this.state.errors.has('employmentId')}>
                                    <FormLabel>{translateKey('employment.employment')}</FormLabel>
                                    <InputGroup>
                                        <Dropdown
                                            fieldKey="employmentId"
                                            placeholder=""
                                            selectOptions={[
                                                {
                                                    value: '',
                                                    text: translateKey(
                                                        'common.selectItem',
                                                        translateKey('employment.employment')
                                                    ),
                                                },
                                            ].concat(
                                                this.state.practitionerEmployments.map((practitionerEmployment) => ({
                                                    value: practitionerEmployment.id,
                                                    text: practitionerEmployment.organization.name,
                                                }))
                                            )}
                                        />
                                    </InputGroup>
                                </FormGroup>
                                {this.state.episodeCategories && (
                                    <FormGroup hasErrors={this.state.errors.has('episodeCategoryId')}>
                                        <FormLabel>{translateKey('service.service')}</FormLabel>
                                        <InputGroup>
                                            <Dropdown
                                                fieldKey="episodeCategoryId"
                                                placeholder=""
                                                selectOptions={[
                                                    {
                                                        value: '',
                                                        text: translateKey(
                                                            'common.selectItem',
                                                            translateKey('service.service')
                                                        ),
                                                    },
                                                ].concat(
                                                    this.state.episodeCategories.map((episodeCategory) => ({
                                                        value: episodeCategory.id,
                                                        text: translateKey(
                                                            `episodeCategoryEnum.${episodeCategory.name}`
                                                        ),
                                                    }))
                                                )}
                                            />
                                        </InputGroup>
                                    </FormGroup>
                                )}
                            </>
                        )}
                        <CancelButton
                            disabled={this.state.isSaving}
                            to={`/practitioners/${practitionerId}?tab=serviceReadersListIsVisible`}
                        />
                        <SaveSubmit disabled={this.state.isSaving || !!this.state.overlap} />
                    </FormContainer>
                </FormContextProvider>
            </FixedWidthTemplate>
        );
    }

    private async setupComponent(props: ServiceReaderAddEditProps) {
        const practitionerId = this.props.router.searchParams.get('practitionerId');
        if (this.getAddOrEdit() === 'add') {
            await this.setEmptyServiceReader();
        }
        if (practitionerId) {
            await this.loadPractitioner(practitionerId!);
            if (this.getAddOrEdit(props.router.params) === 'edit') {
                await this.load(props.router.params.id!);
            } else {
                await this.loadPractitionerEmployments(practitionerId!);
                await this.loadEpisodeCategories();
            }
        }
    }

    private async setEmptyServiceReader() {
        await this.setState({
            serviceReader: {
                startDate: DateTime.now().format('YYYY-MM-DD'),
                endDate: null,
                episodeCategoryId: null,
                employmentId: null,
            },
        });
    }

    private async handleChange<K extends keyof IServiceReader>(name: K, value: IServiceReader[K]) {
        await this.setState(({ serviceReader }) => ({ serviceReader: { ...serviceReader, [name]: value } }));
        if (name === 'employmentId' || name === 'episodeCategoryId' || name === 'startDate' || name === 'endDate') {
            this.checkOverlap();
        }
    }

    private async validateOne(name: keyof IServiceReader) {
        await this.setState(({ serviceReader, errors }) => ({
            errors: updateValidationErrorsForOne(serviceReaderAddEditSchema, serviceReader, errors, name),
        }));
    }

    private async checkOverlap() {
        if (
            this.state.serviceReader &&
            this.state.serviceReader.episodeCategoryId &&
            this.state.serviceReader.employmentId
        ) {
            const overlap = await this.overlapChecker.check(
                {
                    employmentId: this.state.serviceReader.employmentId,
                    episodeCategoryId: this.state.serviceReader.episodeCategoryId,
                },
                this.state.serviceReader.startDate,
                this.state.serviceReader.endDate,
                this.props.router.params.id
            );
            await this.setState({ overlap });
        }
    }

    private getAddOrEdit(paramsArgument?: { id?: string }) {
        const params = paramsArgument || this.props.router.params;

        return params.id ? 'edit' : 'add';
    }

    private async loadPractitioner(practitionerId: string) {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ practitioner: result.json });
    }

    private async loadPractitionerEmployments(practitionerId: string) {
        const url = `/api/employments/${practitionerId}/organizations?isActive=true&isNoManager=true`;
        const results = await fetchJson(url, 'GET');
        if (results.json.items.length > 1) {
            await this.setState({ practitionerEmployments: results.json.items });
        } else if (results.json.items.length === 1) {
            await this.setState(({ serviceReader }) => ({
                serviceReader: {
                    ...serviceReader,
                    employmentId: results.json.items[0].id,
                },
                practitionerEmployments: results.json.items,
            }));
        }
    }

    private async loadEpisodeCategories() {
        const url = '/api/episodeCategories';
        const results = await fetchJson(url, 'GET');
        if (results.json.items.length === 1) {
            await this.setState(({ serviceReader }) => ({
                serviceReader: { ...serviceReader, episodeCategoryId: results.json.items[0].id },
                episodeCategories: results.json.items,
            }));
        } else {
            await this.setState({ episodeCategories: results.json.items });
        }
    }

    private async load(serviceReaderId: string) {
        const url = `/api/serviceReaders/${serviceReaderId}`;
        const results = await fetchJson(url, 'GET');
        await this.setState({
            serviceReader: results.json,
            overlap: undefined,
            episodeCategoryName: results.json.episodeCategory.name,
        });
    }

    private async save() {
        if (this.state.overlap) {
            return;
        }
        let url = '/api/serviceReaders/';
        if (this.getAddOrEdit() === 'edit') {
            url += this.props.router.params.id;
        }
        const { errors, item } = validate(serviceReaderAddEditSchema, this.state.serviceReader);
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        await this.setState({ isSaving: true });
        await fetchJson(url, 'POST', item);
        this.props.router.navigate(
            `/practitioners/${this.props.router.searchParams.get('practitionerId')}?tab=serviceReadersListIsVisible`
        );
    }
}

export const ServiceReaderAddEdit = withRouter(InnerServiceReaderAddEdit);
